<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import Table from "@/components/shared/Table.vue"
import TableImage from "@/components/shared/TableImage.vue"
import router from "@/plugins/router"
import store from "@/plugins/store"
import qualityService, { type UpadateProductRequest } from "@/services/quality"
import type { Option } from "@/types/common.type"
import type { Partner } from "@/types/partner.type"
import type { Product } from "@/types/product.type"
import type { QualityDetail } from "@/types/quality.type"
import { reportSentry } from "@/utils/sentry"

const route = useRoute()
const { t } = useI18n()

const partner = computed<Partner | null>(() => store.getters.partner)
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("item.editColors")} #${item.value?.name || ""}`
})

const item = ref<QualityDetail | null>(null)
const colorProducts = ref<Product[]>([])
const statusOptions = ref<Option[]>([
  { label: t("colorProduct.statusList.published"), value: "published" },
  { label: t("colorProduct.statusList.retired"), value: "retired" },
])

async function loadItem(itemId: number) {
  item.value = await qualityService.get(itemId)
}

async function loadProducts(itemId: number) {
  const data = await qualityService.products(itemId)
  colorProducts.value = data.products
}

onMounted(async () => {
  // id.value is bound to the live route, not a snapshot — if the user
  // navigates away while this is still running, route.params.id changes
  // out from under the second await, sending Number(undefined) = NaN.
  // Same bug fixed in Item.vue (Sentry: qualities/NaN/products).
  const itemId = Number(id.value)
  try {
    await loadItem(itemId)
    await loadProducts(itemId)
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})

const isSubmitting = ref(false)
const errorMessage = ref("")

async function saveColors() {
  isSubmitting.value = true
  errorMessage.value = ""

  const payload = { products: [] } as UpadateProductRequest
  colorProducts.value.forEach((product) => {
    payload.products.push({
      id: product.id,
      storeColorCode: product.storeColorCode,
      priceSurplus: product.priceSurplus,
      status: product.status,
    })
  })

  try {
    await qualityService.updateProducts(Number(id.value), payload)

    router.push(`/items/${id.value}`)
  } catch (error) {
    console.log(error)
    reportSentry(error)
    errorMessage.value = t("shared.errors.unknown")
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <form @submit.prevent="saveColors">
      <Card v-if="partner && item">
        <CardTitle>{{ t("colorProducts") }} ({{ colorProducts.length }})</CardTitle>
        <Table>
          <template #header>
            <tr>
              <th>{{ t("colorProduct.number") }}</th>
              <th>{{ t("colorProduct.image") }}</th>
              <th v-if="item.storeCoded">{{ t("colorProduct.colorCode") }}</th>
              <th>{{ t("colorProduct.colors") }}</th>
              <th>{{ t("colorProduct.priceSurplus") }}</th>
              <th>{{ t("colorProduct.status") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(product, idx) in colorProducts" :key="idx">
              <td>{{ product.colorNumber }}</td>
              <td>
                <TableImage v-if="product.image" :image="product.image" />
                <span v-else>-</span>
              </td>
              <td v-if="item.storeCoded">
                <InputBox v-model="product.storeColorCode" type="text" field="storeColorCode" />
              </td>
              <td>{{ product.colors.map((e) => e.name).join(", ") }}</td>
              <td>
                <InputBox
                  v-model="product.priceSurplus"
                  type="number"
                  field="priceSurplus"
                  :prefix="partner.tradingCurrency.toUpperCase()"
                  :min="0"
                  required
                />
              </td>
              <td>
                <InputSelect v-model="product.status" field="status" :options="statusOptions" required />
              </td>
            </tr>
          </template>
        </Table>
        <CardActions justifyEnd>
          <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
        </CardActions>
        <CardActions displayBlock v-if="errorMessage.length > 0">
          <span class="text-center text-danger error-message">{{ errorMessage }}</span>
        </CardActions>
      </Card>
    </form>
  </LayoutAuthed>
</template>

<style scoped></style>
